// src/components/members/Members.js
import React, { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Image,
  Badge,
  Form,
  InputGroup,
  Button,
  Spinner,
  Pagination
} from 'react-bootstrap'
import { memberService } from '../../services/api'
import { imagaeAPI } from '../../services/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faUser,
  faEnvelope,
  faPhone,
  faBuilding,
  faMapMarkerAlt,
  faFilter,
  faTimesCircle,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons'
import indiaDistricts from '../../statesdistricts/indiaDistricts'

const Members = () => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [showFilters, setShowFilters] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    membership_type: '',
    profession: ''
  })

  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search (300ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const fetchMembers = useCallback(
    async (page = 1) => {
      setLoading(true)
      try {
        const params = { page, per_page: 12 }
        if (debouncedSearch) params.search = debouncedSearch
        if (filters.state) params.state = filters.state
        if (filters.district) params.district = filters.district
        if (filters.membership_type) params.membership_type = filters.membership_type
        if (filters.profession) params.profession = filters.profession

        const response = await memberService.getAll(params)
        setMembers(response.data.data || [])
        setMeta(response.data.meta || { current_page: 1, last_page: 1, total: 0 })
      } catch (error) {
        console.error('Error fetching members:', error)
        setMembers([])
      } finally {
        setLoading(false)
      }
    },
    [debouncedSearch, filters]
  )

  useEffect(() => {
    fetchMembers(1)
  }, [fetchMembers])

  const states = indiaDistricts.map(item => item.state)

  const districts =
    filters.state
      ? indiaDistricts.find(item => item.state === filters.state)?.districts || []
      : []

  const handleFilterChange = e => {
    const { name, value } = e.target
    if (name === 'state') {
      setFilters(prev => ({ ...prev, state: value, district: '' }))
    } else {
      setFilters(prev => ({ ...prev, [name]: value }))
    }
  }

  const clearFilters = () => {
    setFilters({ state: '', district: '', membership_type: '', profession: '' })
    setSearchTerm('')
  }

  const hasActiveFilters =
    searchTerm ||
    filters.state ||
    filters.district ||
    filters.membership_type ||
    filters.profession

  const handlePageChange = page => {
    fetchMembers(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }


  const inputStyle = {
    border: '2px solid #1868b8',
    borderRadius: '8px',
    padding: '10px 14px',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
  }

  

  return (
    <section className='py-5' style={{ backgroundColor: '#f4f7fa', minHeight: '100vh' }}>
      <Container>
        {/* Page Header */}
        <Row className='mb-4'>
          <Col className='text-center'>
            <h1 className='display-5 fw-bold'>Our Members</h1>
            <div
              className='divider mx-auto'
              style={{
                width: '70px',
                height: '4px',
                background: 'linear-gradient(90deg, #0056b3, #007bff)',
                margin: '1rem auto 1rem'
              }}
            />
            <p className='text-muted lead'>
              Connect with media professionals across India
            </p>
          </Col>
        </Row>

       {/* Search Bar */}
<Row className='mb-4'>
  <Col lg={8} className='mx-auto'>
    <InputGroup className='shadow-sm'>
      <InputGroup.Text className='bg-white border-end-0'>
        <FontAwesomeIcon icon={faSearch} className='text-primary' />
      </InputGroup.Text>
      <Form.Control
        type='text'
        placeholder='Search by name, city, district, state...'
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className=''
        style={inputStyle}
      />
      {hasActiveFilters && (
        <Button
          variant='outline-danger'
          onClick={clearFilters}
        >
          <FontAwesomeIcon icon={faTimesCircle} className='me-2' />
          Clear
        </Button>
      )}
    </InputGroup>
  </Col>
</Row>

{/* Filter Panel — always visible */}
<Row className='mb-4'>
  <Col lg={10} className='mx-auto'>
    <Card className='border-0 shadow-sm'>
      <Card.Body>
        <div className='d-flex align-items-center mb-3'>
          <FontAwesomeIcon icon={faFilter} className='text-primary me-2' />
          <h6 className='fw-bold mb-0'>Filter Members</h6>
        </div>

        <Row>
          <Col md={3} className='mb-3 mb-md-0'>
            <Form.Label className='fw-semibold small'>State</Form.Label>
            <Form.Select
              name='state'
              value={filters.state}
              onChange={handleFilterChange}
              style={inputStyle}
            >
              <option value=''>All States</option>
              {states.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3} className='mb-3 mb-md-0'>
            <Form.Label className='fw-semibold small'>District</Form.Label>
            <Form.Select
              name='district'
              value={filters.district}
              onChange={handleFilterChange}
              disabled={!filters.state}
              style={inputStyle}
            >
              <option value=''>
                {filters.state ? 'All Districts' : 'Select state first'}
              </option>
              {districts.map(d => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3} className='mb-3 mb-md-0'>
            <Form.Label className='fw-semibold small'>
              Membership Type
            </Form.Label>
            <Form.Select
              name='membership_type'
              value={filters.membership_type}
              onChange={handleFilterChange}
              style={inputStyle}
            >
              <option value=''>All Types</option>
              <option value='Professional Media Member'>
                Professional Media Member
              </option>
              <option value='Student Media Member'>
                Student Media Member
              </option>
              <option value='Corporate Member'>Corporate Member</option>
              <option value='Business Member'>Business Member</option>
              <option value='General Member'>General Member</option>
            </Form.Select>
          </Col>

          <Col md={3} className='mb-3 mb-md-0'>
            <Form.Label className='fw-semibold small'>Profession</Form.Label>
            <Form.Select
              name='profession'
              value={filters.profession}
              onChange={handleFilterChange}
              style={inputStyle}
            >
              <option value=''>All Professions</option>
              <option value='journalist'>Journalist</option>
              <option value='editor'>Editor</option>
              <option value='reporter'>Reporter</option>
              <option value='photographer'>Photographer</option>
              <option value='videographer'>Videographer</option>
              <option value='writer'>Writer/Author</option>
              <option value='poet'>Poet</option>
              <option value='artist'>Artist</option>
              <option value='theater'>Theater Artist</option>
              <option value='media_educator'>Media Educator</option>
              <option value='student'>Media Student</option>
              <option value='other'>Other</option>
            </Form.Select>
          </Col>
        </Row>

        {hasActiveFilters && (
          <div className='text-end mt-3'>
            <Button
              variant='link'
              className='text-danger text-decoration-none'
              onClick={clearFilters}
            >
              <FontAwesomeIcon icon={faTimesCircle} className='me-1' />
              Clear All Filters
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  </Col>
</Row>

        {/* Results Summary */}
        {!loading && (
          <Row className='mb-3'>
            <Col className='text-center'>
              <p className='text-muted mb-0'>
                {meta.total > 0
                  ? `Showing ${members.length} of ${meta.total} member${meta.total !== 1 ? 's' : ''}`
                  : 'No members found'}
              </p>
            </Col>
          </Row>
        )}

        {/* Members Grid */}
        <Row>
          {loading ? (
            <Col className='text-center py-5'>
              <Spinner animation='border' variant='primary' />
              <p className='text-muted mt-3'>Loading members...</p>
            </Col>
          ) : members.length > 0 ? (
            members.map(member => (
              <Col lg={4} md={6} key={member.id} className='mb-4'>
                <Card className='h-100 shadow-sm border-0 member-card'>
                  <Card.Body className='text-center'>
                    <div className='member-image-wrapper mb-3'>
                      {member.profile_image ? (
                        <Image
                          src={`${imagaeAPI}/${member.profile_image}`}
                          
                          className='member-image'
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            border: '3px solid #1868b8'
                          }}
                        />
                      ) : (
                        <div
                          className='member-image-placeholder d-flex align-items-center justify-content-center mx-auto'
                          style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            backgroundColor: '#e3f2fd',
                            border: '3px solid #1868b8'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faUser}
                            size='2x'
                            className='text-primary'
                          />
                        </div>
                      )}
                    </div>

                    <h5 className='fw-bold mb-1'>{member.name}</h5>

                    {member.id && (
                      <p className='text-primary mb-1 small fw-semibold'>
                        ID: INMA{member.id}
                      </p>
                    )}

                    {/* {member.organization && (
                      <p className='text-muted small mb-2'>
                        <FontAwesomeIcon icon={faBuilding} className='me-1' />
                        {member.organization}
                      </p>
                    )} */}

                    <Badge bg='primary' className='mb-3'>
                      {member.membership_type}
                    </Badge>

                    <div className='member-contact small'>
                      {(member.state || member.district) && (
                        <p className='mb-1 text-muted'>
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className='me-2 text-primary'
                          />
                          {[member.district, member.state]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      )}

                      {/* {member.expertise && (
                        <p className='mb-1 text-muted'>
                          <FontAwesomeIcon
                            icon={faBriefcase}
                            className='me-2 text-primary'
                          />
                          {member.expertise}
                        </p>
                      )} */}

                      {/* <p className='mb-1 text-muted'>
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className='me-2 text-primary'
                        />
                        <a href={`mailto:${member.email}`} className='text-decoration-none'>
                          {member.email}
                        </a>
                      </p> */}

                      {/* {member.phone && (
                        <p className='mb-0 text-muted'>
                          <FontAwesomeIcon
                            icon={faPhone}
                            className='me-2 text-primary'
                          />
                          <a href={`tel:${member.phone}`} className='text-decoration-none'>
                            {member.phone}
                          </a>
                        </p>
                      )} */}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col>
              <div className='text-center py-5'>
                <FontAwesomeIcon
                  icon={faSearch}
                  size='3x'
                  className='text-muted mb-3'
                />
                <h4>No members found</h4>
                <p className='text-muted'>
                  Try adjusting your search or filters
                </p>
                {hasActiveFilters && (
                  <Button variant='primary' onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
            </Col>
          )}
        </Row>

        {/* Pagination */}
        {!loading && meta.last_page > 1 && (
          <Row className='mt-4'>
            <Col className='d-flex justify-content-center'>
              <Pagination>
                <Pagination.Prev
                  disabled={meta.current_page === 1}
                  onClick={() => handlePageChange(meta.current_page - 1)}
                />
                {[...Array(meta.last_page)].map((_, i) => {
                  const page = i + 1
                  // Show first, last, current, and neighbors
                  if (
                    page === 1 ||
                    page === meta.last_page ||
                    Math.abs(page - meta.current_page) <= 1
                  ) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === meta.current_page}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  } else if (
                    page === meta.current_page - 2 ||
                    page === meta.current_page + 2
                  ) {
                    return <Pagination.Ellipsis key={page} disabled />
                  }
                  return null
                })}
                <Pagination.Next
                  disabled={meta.current_page === meta.last_page}
                  onClick={() => handlePageChange(meta.current_page + 1)}
                />
              </Pagination>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  )
}

export default Members